import React, { useMemo, useState } from 'react';
import { ApolloError, gql, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { describeError, getRateLimitRejection } from '../../../../common/rateLimit';
import { WHATSAPP_TEMPLATES } from '../../../whatsapp/whatsapp.queries';
import {
  isApproved,
  missingVariables,
  previewBody,
  toVariablesInput,
  WhatsAppTemplate,
} from '../../../whatsapp/whatsapp';

/**
 * One WhatsApp message to one person, from their page (`crmWhatsAppContact`):
 * free text or an approved template, to their main number. The API decides who
 * can be messaged (`crmContactWhatsApp`, the CRM's texting rule) and this page
 * only shows its answer: the button when they can, a banner saying why when
 * they cannot. Outside production a send is logged, not delivered.
 */

export const CRM_CONTACT_WHATSAPP = gql`
  query CrmContactWhatsApp($contactId: Int!) {
    crmContactWhatsApp(contactId: $contactId) {
      canSend
      reason
      message
      phone
      dryRun
    }
  }
`;

export const CRM_WHATSAPP_CONTACT = gql`
  mutation CrmWhatsAppContact($input: CrmWhatsAppContactInput!) {
    crmWhatsAppContact(input: $input) {
      phone
      dryRun
      status
      delivered
      errorCode
      explanation
      noteKept
    }
  }
`;

export interface ContactWhatsApp {
  canSend: boolean;
  reason: string | null;
  message: string | null;
  phone: string | null;
  dryRun: boolean;
}

interface SendResult {
  phone: string;
  dryRun: boolean;
  status: string | null;
  delivered: boolean;
  errorCode: number | null;
  explanation: string;
  noteKept: boolean;
}

export const MAX_WHATSAPP_TEXT = 1600;

/**
 * Why they cannot be sent a WhatsApp message, for the page. Nothing for a
 * merged record: the merged banner above already sends the admin elsewhere.
 */
export const WhatsAppBanner = ({ status, name }: { status: ContactWhatsApp | undefined; name: string }) =>
  !status || status.canSend || status.reason === 'MERGED' ? null : (
    <Alert severity="info" sx={{ mb: 2 }} data-testid="crm-whatsapp-banner">
      Can’t send WhatsApp to {name}: {status.message}
    </Alert>
  );

const describeSendError = (error: unknown) => {
  if (getRateLimitRejection(error)) return describeError(error, 'Too many messages at once');
  const first = error instanceof ApolloError ? error.graphQLErrors?.[0] : undefined;
  if (first?.message) return first.message;
  return 'The message could not be sent. Nothing was sent.';
};

interface WhatsAppPersonDialogProps {
  contactId: number;
  name: string;
  status: ContactWhatsApp;
  onClose: () => void;
  /** After a send that kept a note, so the page can show it. */
  onSent: (result: SendResult) => void;
}

export const WhatsAppPersonDialog = ({ contactId, name, status, onClose, onSent }: WhatsAppPersonDialogProps) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));

  const templatesQuery = useQuery<{ whatsAppTemplates: WhatsAppTemplate[] }>(WHATSAPP_TEMPLATES);
  const approved = useMemo(
    () => (templatesQuery.data?.whatsAppTemplates ?? []).filter(isApproved),
    [templatesQuery.data],
  );

  const [chosenMode, setMode] = useState<'template' | 'text' | null>(null);
  // A template when there is one: it is the only kind that reaches someone who has not written to us.
  const mode = chosenMode ?? (approved.length ? 'template' : 'text');
  const [text, setText] = useState(() => `Hi ${name}, `);
  const [contentSid, setContentSid] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [result, setResult] = useState<SendResult | null>(null);

  const [send, { loading, error }] = useMutation<{ crmWhatsAppContact: SendResult }>(CRM_WHATSAPP_CONTACT);

  const template = approved.find((t) => t.sid === contentSid);
  const textProblem = !text.trim() ? 'Write the message.' : text.trim().length > MAX_WHATSAPP_TEXT ? `At most ${MAX_WHATSAPP_TEXT} characters.` : '';
  const ready = mode === 'text' ? !textProblem : !!template && missingVariables(template, values).length === 0;

  const go = async () => {
    const input =
      mode === 'text'
        ? { contactId, text: text.trim() }
        : { contactId, contentSid: template!.sid, variables: toVariablesInput(template!, values) };
    try {
      const { data } = await send({ variables: { input } });
      if (data) {
        setResult(data.crmWhatsAppContact);
        if (data.crmWhatsAppContact.noteKept) onSent(data.crmWhatsAppContact);
      }
    } catch {
      // Shown below.
    }
  };

  return (
    <Dialog open onClose={loading ? undefined : onClose} fullScreen={phone} fullWidth maxWidth="sm" aria-labelledby="crm-whatsapp-person-title">
      <DialogTitle id="crm-whatsapp-person-title">WhatsApp {name}</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            To
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {status.phone}
          </Typography>
        </Box>
        {status.dryRun && (
          <Alert severity="warning">This server only logs WhatsApp to contacts: nothing will be delivered and no note kept.</Alert>
        )}

        {result ? (
          <Alert
            severity={result.dryRun ? 'warning' : result.delivered ? 'success' : result.status === 'failed' || result.status === 'undelivered' ? 'error' : 'info'}
            data-testid="crm-whatsapp-result"
          >
            {result.explanation}
            {result.noteKept && ' A copy is in their notes.'}
          </Alert>
        ) : (
          <>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={mode}
              onChange={(_, next) => next && setMode(next)}
              aria-label="What to send"
              disabled={loading}
            >
              <ToggleButton value="template" disabled={!approved.length}>
                Template
              </ToggleButton>
              <ToggleButton value="text">Message</ToggleButton>
            </ToggleButtonGroup>

            {mode === 'text' ? (
              <TextField
                label="Message"
                value={text}
                onChange={(event) => setText(event.target.value)}
                error={!!textProblem && text.length > 0}
                helperText={
                  (text.length > 0 && textProblem) ||
                  'WhatsApp only delivers free text to someone who messaged us in the last 24 hours. For anyone else, use a template.'
                }
                multiline
                minRows={4}
                maxRows={12}
                autoFocus
                disabled={loading}
              />
            ) : (
              <>
                <TextField
                  select
                  label="Template"
                  value={contentSid}
                  onChange={(event) => {
                    setContentSid(event.target.value);
                    setValues({});
                  }}
                  disabled={loading}
                >
                  {approved.map((t) => (
                    <MenuItem key={t.sid} value={t.sid}>
                      {t.name} ({t.language})
                    </MenuItem>
                  ))}
                </TextField>
                {template?.variables.map((key) => (
                  <TextField
                    key={key}
                    label={`{{${key}}}`}
                    value={values[key] ?? ''}
                    onChange={(event) => setValues({ ...values, [key]: event.target.value })}
                    helperText={`One line. {firstName} becomes ${name}.`}
                    disabled={loading}
                  />
                ))}
                {template && (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    {previewBody(template, values, name)}
                  </Typography>
                )}
              </>
            )}
            <Typography variant="caption" color="text.secondary">
              It goes from our WhatsApp number to their main number. Unless WhatsApp refuses it, a copy is kept as a note on their record.
            </Typography>
          </>
        )}
        {error && <Alert severity="error">{describeSendError(error)}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {result ? (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={go}
              disabled={loading || !ready}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {loading ? 'Sending… (up to 20s)' : 'Send'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
